import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { trackEvent } from '../../services/analyticsService';

interface FeedbackResponse {
  userId: string;
  stage: number;
  responses: Record<string, any>;
  submittedAt: number;
}

const FeedbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [responses, setResponses] = useState<Record<string, any>>({});

  const userId = searchParams.get('u');
  const stage = parseInt(searchParams.get('s') || '1');


  useEffect(() => {
    trackEvent('feedback_page_view', {
      stage: stage,
      userId: userId
    });
  }, [stage, userId]);

  const submitFeedback = async (feedbackResponses: Record<string, any>) => {
    if (!userId) return;

    setIsSubmitting(true);

    try {
      const functions = getFunctions();
      const submitFeedbackFunc = httpsCallable(functions, 'submitFeedback');

      const response: FeedbackResponse = {
        userId,
        stage,
        responses: feedbackResponses,
        submittedAt: Date.now()
      };

      await submitFeedbackFunc(response);

      setIsSubmitted(true);
      trackEvent('feedback_submitted', {
        stage: stage,
        userId: userId
      });

      // Redirect to thank you page after a delay
      setTimeout(() => {
        navigate('/feedback/thanks');
      }, 2000);

    } catch (error) {
      console.error('Error submitting feedback:', error);
      trackEvent('feedback_submit_error', {
        stage: stage,
        userId: userId,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResponseChange = (questionId: string, value: any) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const getStageContent = () => {
    // Same comprehensive questions for all stages - follow-ups give users multiple chances to respond
    return {
      title: "Help us improve AwakenArc!",
      subtitle: "Your feedback helps us build a better experience for everyone",
      questions: [
        {
          id: 'user_type',
          type: 'radio',
          question: 'Which best describes you?',
          options: [
            'Student or learner',
            'Professional looking to improve productivity',
            'Entrepreneur or freelancer',
            'Someone working on personal development',
            'Parent or caregiver',
            'Retired or semi-retired',
            'Other'
          ]
        },
        {
          id: 'heard_about',
          type: 'radio',
          question: 'How did you first hear about AwakenArc?',
          options: [
            'Social media (Instagram, TikTok, etc.)',
            'Search engine (Google, etc.)',
            'Friend or colleague recommendation',
            'App store browsing',
            'Online article/blog',
            'YouTube or video content',
            'Other'
          ]
        },
        {
          id: 'usage_frequency',
          type: 'radio',
          question: 'How often have you been using AwakenArc?',
          options: [
            'Daily (multiple times per day)',
            'Daily (once per day)',
            'Several times per week',
            'Once per week',
            'A few times total',
            'Just trying it out'
          ]
        },
        {
          id: 'main_motivation',
          type: 'radio',
          question: 'What\'s your primary motivation for using AwakenArc?',
          options: [
            'Break bad habits and build good ones',
            'Increase productivity and focus',
            'Track personal growth and achievements',
            'Manage time and organize daily life',
            'Achieve specific personal goals',
            'Overcome procrastination',
            'Improve work-life balance',
            'Something else'
          ]
        },
        {
          id: 'satisfaction',
          type: 'radio',
          question: 'Overall, how satisfied are you with AwakenArc?',
          options: ['Very satisfied', 'Somewhat satisfied', 'Neutral', 'Somewhat dissatisfied', 'Very dissatisfied']
        },
        {
          id: 'favorite_feature',
          type: 'radio',
          question: 'What\'s your favorite feature?',
          options: [
            'Quest system and gamification',
            'Calendar integration',
            'Achievements and rewards',
            'Passive skills tracking',
            'Progress tracking',
            'AI assistance',
            'Something else'
          ]
        },
        {
          id: 'nps_score',
          type: 'radio',
          question: 'How likely are you to recommend AwakenArc to a friend? (0-10)',
          options: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']
        },
        {
          id: 'most_helpful',
          type: 'textarea',
          question: 'What has been most helpful about AwakenArc?',
          placeholder: 'Specific features, experiences, or outcomes that stood out...'
        },
        {
          id: 'current_challenges',
          type: 'textarea',
          question: 'What\'s your biggest challenge that you hope AwakenArc will help with?',
          placeholder: 'Be as specific as possible - this helps us improve...'
        },
        {
          id: 'improvement_suggestions',
          type: 'textarea',
          question: 'What could we improve or add to make AwakenArc better?',
          placeholder: 'Any frustrations, missing features, or suggestions?'
        },
        {
          id: 'likelihood_to_continue',
          type: 'radio',
          question: 'How likely are you to continue using AwakenArc?',
          options: [
            'Very likely - I love it!',
            'Somewhat likely - it\'s helpful',
            'Neutral - still deciding',
            'Somewhat unlikely - not meeting my needs',
            'Very unlikely - probably won\'t continue'
          ]
        }
      ]
    };
  };

  const content = getStageContent();

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-dark-900 to-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-dark-800 rounded-2xl p-8 text-center border border-system-blue/20">
          <div className="text-6xl mb-4">🎉</div>
          <h1 className="text-2xl font-bold text-white mb-4 font-rajdhani">Thank You!</h1>
          <p className="text-gray-300 mb-6">
            Your feedback helps us make AwakenArc better for everyone. We truly appreciate you taking the time to share your thoughts.
          </p>
          <div className="text-system-blue text-sm">
            Redirecting you shortly...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-dark-900 to-gray-900">
      <div className="max-w-2xl mx-auto px-4 py-4 sm:py-8">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center gap-3 bg-dark-800/50 rounded-full px-4 py-2 sm:px-6 sm:py-3 mb-4 sm:mb-6 border border-system-blue/20">
            <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-system-blue to-accent-gold rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-xs sm:text-sm">A</span>
            </div>
            <span className="text-white font-rajdhani font-semibold text-sm sm:text-base">AwakenArc</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2 font-rajdhani leading-tight">{content.title}</h1>
          <p className="text-gray-400 text-sm sm:text-base px-2">{content.subtitle}</p>
        </div>

        {/* Questions */}
        <div className="bg-dark-800 rounded-2xl p-4 sm:p-6 border border-system-blue/20">
          <form onSubmit={(e) => {
            e.preventDefault();
            submitFeedback(responses);
          }} className="space-y-6">
            {content.questions.map((question, index) => (
              <div key={question.id} className="space-y-3 sm:space-y-4">
                <label className="block text-white font-medium text-sm sm:text-base leading-relaxed">
                  {index + 1}. {question.question}
                </label>

                {question.type === 'radio' && (
                  <div className="space-y-2">
                    {question.options?.map((option) => (
                      <div key={option}>
                        <label className="flex items-start gap-3 cursor-pointer hover:bg-dark-700/50 p-3 sm:p-2 rounded-lg transition-colors touch-manipulation">
                          <input
                            type="radio"
                            name={question.id}
                            value={option}
                            onChange={(e) => handleResponseChange(question.id, e.target.value)}
                            className="w-4 h-4 sm:w-4 sm:h-4 text-system-blue bg-dark-700 border-gray-600 focus:ring-system-blue focus:ring-offset-0 mt-0.5 flex-shrink-0"
                          />
                          <span className="text-gray-300 text-sm sm:text-base leading-relaxed">{option}</span>
                        </label>
                        {/* Show input field for "Other" or "Something else" options */}
                        {(option === 'Other' || option === 'Something else') && responses[question.id] === option && (
                          <div className="ml-7 sm:ml-7 mt-2">
                            <input
                              type="text"
                              placeholder={`Please specify${option === 'Other' ? ' how you heard about us' : ' your goal'}...`}
                              onChange={(e) => handleResponseChange(`${question.id}_other`, e.target.value)}
                              className="w-full px-3 py-3 sm:px-3 sm:py-2 bg-dark-700 border border-gray-600 rounded-md sm:rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-system-blue text-sm sm:text-sm touch-manipulation"
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {question.type === 'textarea' && (
                  <textarea
                    placeholder={question.placeholder}
                    onChange={(e) => handleResponseChange(question.id, e.target.value)}
                    className="w-full px-4 py-3 bg-dark-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-system-blue resize-vertical min-h-[100px] sm:min-h-[100px] text-sm sm:text-base touch-manipulation"
                    rows={4}
                  />
                )}
              </div>
            ))}

            <div className="pt-4 sm:pt-6 border-t border-gray-700">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-system-blue to-accent-gold text-white font-semibold py-4 sm:py-4 px-6 rounded-lg hover:from-system-blue/90 hover:to-accent-gold/90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-rajdhani text-base sm:text-base touch-manipulation min-h-[48px] sm:min-h-[auto]"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span className="text-sm sm:text-base">Submitting...</span>
                  </div>
                ) : (
                  <span className="text-sm sm:text-base">Submit Feedback</span>
                )}
              </button>

              <p className="text-center text-gray-500 text-xs sm:text-sm mt-3 sm:mt-4 px-2">
                Your responses are anonymous and help us improve AwakenArc for everyone.
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FeedbackPage;
